import { ConnectedRouter as Router } from "connected-react-router";
import { Component } from "react";
import { connect } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { getCookie, getCsrfCookie } from "../actions/API";
import AdminRoute from "./AdminRoute";
import asyncComponent from "./AsyncComponent";
import MaintenanceMode from "./MaintenanceMode";

// Dynamically import Route Components for code splitting

const AsyncCallToAction = asyncComponent(
  () => import("./CallToAction/CallToAction"),
);
const AsyncNewCTA = asyncComponent(() => import("./CallToAction/NewCTA"));
const AsyncCSVUploader = asyncComponent(
  () => import("./MessageCentre/CSVUploader"),
);
const AsyncSMSMessageCentre = asyncComponent(
  () => import("./MessageCentre/SMSMessageCentre"),
);
const AsyncUser = asyncComponent(() => import("./User/User"));
const AsyncNewUser = asyncComponent(() => import("./User/NewUser"));
const AsyncCompany = asyncComponent(() => import("./Company/Company"));
const AsyncCoachingRequests = asyncComponent(
  () => import("./CoachingRequests/CoachingRequests"),
);
const AsyncCoachingActions = asyncComponent(
  () => import("./CoachingRequests/CoachingRequestForCallLogs"),
);
const AsyncNewCompany = asyncComponent(() => import("./Company/NewCompany"));
const AsyncDocument = asyncComponent(() => import("./Document/Document"));
const AsyncNewDocument = asyncComponent(() => import("./Document/NewDocument"));
const AsyncPost = asyncComponent(() => import("./Post/Post"));
const AsyncNewPost = asyncComponent(() => import("./Post/NewPost"));
const AsyncReport = asyncComponent(() => import("./Report/Report"));
const AsyncReportType = asyncComponent(() => import("./Report/ReportType"));
const AsyncNewContactPoint = asyncComponent(
  () => import("./ContactPoints/NewContactPoint"),
);
const AsyncEditContactPoint = asyncComponent(
  () => import("./ContactPoints/EditContactPoint"),
);
const AsyncCreateSMS = asyncComponent(
  () => import("./MessageCentre/CreateSMS"),
);
const AsyncCreateBulkSMS = asyncComponent(
  () => import("./MessageCentre/CreateBulkSMS"),
);
const AsyncDeleteContactPoint = asyncComponent(
  () => import("./ContactPoints/DeleteContactPoint"),
);
const AsyncCoachingThemes = asyncComponent(
  () => import("./ContactPoints/CoachingThemes"),
);
const AsyncAudit = asyncComponent(() => import("./Audits/Audits"));
const AsyncNewAppointment = asyncComponent(
  () => import("./Appointments/NewAppointment"),
);
const AsyncEditAppointment = asyncComponent(
  () => import("./Appointments/EditAppointment"),
);
const AsyncSurveys = asyncComponent(() => import("./Surveys/Surveys"));
const AsyncSurveyEditor = asyncComponent(
  () => import("./Surveys/SurveyEditor"),
);
const AsyncQuestions = asyncComponent(() => import("./Surveys/Questions"));
const AsyncQuestionEditor = asyncComponent(
  () => import("./Surveys/QuestionEditor"),
);

const AsyncDashboard = asyncComponent(() => import("./Dashboard/Dashboard"));
const AsyncAuthError = asyncComponent(() => import("./Dashboard/AuthError"));
const AsyncLogin = asyncComponent(() => import("./Login"));
const AsyncUsers = asyncComponent(() => import("./Users"));
const AsyncCompanies = asyncComponent(() => import("./Companies"));
const AsyncBlog = asyncComponent(() => import("./Blog"));
const AsyncNotFound = asyncComponent(() => import("./NotFound"));

const AsyncStories = asyncComponent(() => import("./Stories/ViewStories"));
const AsyncCreateStories = asyncComponent(
  () => import("./Stories/CreateStories"),
);
const AsyncEditStories = asyncComponent(() => import("./Stories/EditStories"));
const AsyncEditStoryTemplates = asyncComponent(
  () => import("./Stories/EditStoryTemplates"),
);

const AsyncViewStoryTemplates = asyncComponent(
  () => import("./Stories/ViewStoryTemplates"),
);

const AsyncCards = asyncComponent(() => import("./Cards/Cards"));
const AsyncCard = asyncComponent(() => import("./Cards/Card"));

const AsyncSteps = asyncComponent(() => import("./Cards/Steps"));
const AsyncStep = asyncComponent(() => import("./Cards/Step"));

const AsyncACL = asyncComponent(() => import("./ACL/CoachingListView"));

const AsyncPermissionsDashboard = asyncComponent(
  () => import("./Permissions/PermissionsDashboard"),
);

const AsyncRoleDisplay = asyncComponent(
  () => import("./Permissions/RoleDisplay"),
);

const AsyncPermissionsEdit = asyncComponent(
  () => import("./Permissions/PermissionsEdit"),
);

class App extends Component {
  state = {
    up: true,
  };

  componentDidMount = async () => {
    // If server is unreachable / down, we render a maintenance page
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    await getCsrfCookie();
    const xsrfToken = getCookie("XSRF-TOKEN");

    let up = await fetch(`${apiEndpoint}/up`, {
      method: "GET",
      headers: {
        "X-CSRF-TOKEN": xsrfToken,
      },
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => data)
      .catch((e) => this.setState({ up: false }));
    this.setState({ up });
  };
  render() {
    const up = this.state.up;
    if (up) {
      return (
        <Router history={this.props.history}>
          <Switch>
            {/* v2 start */}
            <AdminRoute exact path="/cards" component={AsyncCards} />
            <AdminRoute exact path="/card/new" component={AsyncCard} />
            <AdminRoute exact path="/card/:id" component={AsyncCard} />
            <AdminRoute exact path="/steps" component={AsyncSteps} />
            <AdminRoute exact path="/step/new" component={AsyncStep} />
            <AdminRoute exact path="/step/:id" component={AsyncStep} />
            {/* v2 end */}
            <Route exact path="/login" component={AsyncLogin} />
            <AdminRoute exact path="/" component={AsyncDashboard} />
            <AdminRoute exact path="/dashboard" component={AsyncDashboard} />
            <AdminRoute
              exact
              path="/calls-to-action"
              component={AsyncCallToAction}
            />
            <AdminRoute
              exact
              path="/calls-to-action/new"
              component={AsyncNewCTA}
            />
            <AdminRoute exact path="/stories" component={AsyncStories} />
            <AdminRoute
              exact
              path="/create-stories"
              component={AsyncCreateStories}
            />
            <AdminRoute
              exact
              path="/stories/templates"
              component={AsyncViewStoryTemplates}
            />
            <AdminRoute
              exact
              path="/stories/:id"
              component={AsyncEditStories}
            />
            <AdminRoute
              exact
              path="/stories/edit-template/:id"
              component={AsyncEditStoryTemplates}
            />

            <AdminRoute exact path="/surveys" component={AsyncSurveys} />
            <AdminRoute
              exact
              path="/survey/edit/:id"
              component={AsyncSurveyEditor}
            />
            <AdminRoute exact path="/questions" component={AsyncQuestions} />
            <AdminRoute
              exact
              path="/question/edit/:id"
              component={AsyncQuestionEditor}
            />

            <AdminRoute exact path="/users" component={AsyncUsers} />
            <AdminRoute exact path="/user/new" component={AsyncNewUser} />
            <AdminRoute exact path="/user/:id" component={AsyncUser} />
            <AdminRoute
              exact
              path="/scheduling/new"
              component={AsyncNewAppointment}
            />
            <AdminRoute
              exact
              path="/scheduling/edit"
              component={AsyncEditAppointment}
            />
            <AdminRoute exact path="/companies" component={AsyncCompanies} />
            <AdminRoute exact path="/company/new" component={AsyncNewCompany} />
            <AdminRoute exact path="/company/:id" component={AsyncCompany} />
            <AdminRoute
              exact
              path="/company/:company_id/document/new"
              component={AsyncNewDocument}
            />
            <AdminRoute
              exact
              path="/company/:company_id/document/:id"
              component={AsyncDocument}
            />
            <AdminRoute
              exact
              path="/coaching-requests"
              component={AsyncCoachingRequests}
            />
            <AdminRoute
              exact
              path="/coaching-actions"
              component={AsyncCoachingActions}
            />
            <AdminRoute exact path="/blog" component={AsyncBlog} />
            <AdminRoute exact path="/blog/new" component={AsyncNewPost} />
            <AdminRoute exact path="/blog/:slug" component={AsyncPost} />
            <AdminRoute exact path="/report" component={AsyncReport} />
            <AdminRoute
              exact
              path="/report/:type"
              component={AsyncReportType}
            />
            <AdminRoute
              exact
              path="/sms/:id/create"
              component={AsyncCreateSMS}
            />
            <AdminRoute
              exact
              path="/sms-list/:id/create"
              component={AsyncCreateBulkSMS}
            />
            <AdminRoute
              exact
              path="/contact_point/:id/new"
              component={AsyncNewContactPoint}
            />
            <AdminRoute
              exact
              path="/contact_point/:id/edit"
              component={AsyncEditContactPoint}
            />
            <AdminRoute
              exact
              path="/contact_point/:id/delete"
              component={AsyncDeleteContactPoint}
            />
            <AdminRoute
              exact
              path="/coaching-themes"
              component={AsyncCoachingThemes}
            />
            <AdminRoute exact path="/auth_error" component={AsyncAuthError} />
            <AdminRoute exact path="/audits" component={AsyncAudit} />
            <AdminRoute
              exact
              path="/sms-message-centre"
              component={AsyncSMSMessageCentre}
            />
            <AdminRoute
              exact
              path="/list-uploader"
              component={AsyncCSVUploader}
            />
            <AdminRoute
              path="/active-coaching-list/search"
              component={AsyncACL}
            />

            <AdminRoute
              exact
              path="/roles"
              component={AsyncPermissionsDashboard}
            />
            <AdminRoute
              exact
              path="/role/:id/edit"
              component={AsyncRoleDisplay}
            />
            <AdminRoute
              exact
              path="/permissions"
              component={AsyncPermissionsEdit}
            />

            <Route exact path="/404" component={AsyncNotFound} />
            <Route component={AsyncNotFound} />
          </Switch>
        </Router>
      );
    } else {
      return <MaintenanceMode />;
    }
  }
}

export default connect()(App);
